import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import LocationTimezoneSettings from '../../components/LocationTimezoneSettings';
import PersonalProfileSettings from '../../components/PersonalProfileSettings';
import AIComplianceWidget from '../../components/AIComplianceWidget';
import BillingTab from '../../components/BillingTab';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import AreaCodePicker from '../../components/AreaCodePicker';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthHeaders } from '../../lib/api';
import { normalizeNaPhoneCountry } from '../../lib/na-phone-countries';

function resolvePhoneCountry(userProfile) {
  if (!userProfile) return 'US';
  if (userProfile.preferred_area_country) {
    return normalizeNaPhoneCountry(userProfile.preferred_area_country);
  }
  if (userProfile.location_country) {
    return normalizeNaPhoneCountry(userProfile.location_country);
  }
  return 'US';
}

const SETTINGS_TAB_IDS = new Set([
  'location-schedule',
  'message-personalization',
  'phone-number',
  'ai-compliance',
  'billing',
  'profile',
  'notifications',
]);

function resolveSettingsTab(tabFromUrl) {
  if (tabFromUrl && SETTINGS_TAB_IDS.has(tabFromUrl)) return tabFromUrl;
  return 'location-schedule';
}

const Settings = () => {
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(() => resolveSettingsTab(tabFromUrl));
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingMessage, setBillingMessage] = useState(null);
  const { user, userProfile, reloadProfile } = useAuth();
  const [a2pStatus, setA2pStatus] = useState(null);
  const [a2pLoading, setA2pLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  // Phone number management state
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneChangeMode, setPhoneChangeMode] = useState(false);
  const [newAreaCode, setNewAreaCode] = useState('');
  const [phoneChanging, setPhoneChanging] = useState(false);
  const [phoneMessage, setPhoneMessage] = useState(null);
  const [checkingAreaCode, setCheckingAreaCode] = useState(false);
  const [areaCodePickerKey, setAreaCodePickerKey] = useState(0);
  const [phoneCountry, setPhoneCountry] = useState('US');

  // Profile tab state
  const [profileForm, setProfileForm] = useState({ fullName: '', phone: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);

  const tabs = [
    { id: 'location-schedule', label: 'Location & Schedule', icon: 'MapPin' },
    { id: 'message-personalization', label: 'Message Personalization', icon: 'MessageCircle' },
    { id: 'phone-number', label: 'Phone Number', icon: 'Phone' },
    { id: 'ai-compliance', label: 'AI Compliance', icon: 'Shield' },
    { id: 'billing', label: 'Billing', icon: 'CreditCard' },
    { id: 'profile', label: 'Profile', icon: 'User' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' }
  ];

  const loadA2pStatus = async () => {
    if (!user?.id) return;
    setA2pLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/a2p/status?refresh=1', { headers });
      const data = await res.json();
      if (res.ok && data) {
        const status = data.data || data;
        setA2pStatus(status);
        const approved = status.a2pStatus === 'approved' || status.campaignStatus === 'VERIFIED';
        if (approved) reloadProfile?.();
      } else {
        setA2pStatus(null);
      }
    } catch {
      setA2pStatus(null);
    } finally {
      setA2pLoading(false);
    }
  };

  const handleA2pComplete = () => {
    loadA2pStatus();
    reloadProfile?.();
  };

  const handleA2pReset = async () => {
    if (!user?.id) return;
    setResetting(true);
    try {
      const headers = await getAuthHeaders();
      await fetch('/api/a2p/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ agentId: user.id }),
      });
      setA2pStatus(null);
      reloadProfile?.();
    } catch (e) {
      // ignore
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    setActiveTab(resolveSettingsTab(tabFromUrl));
  }, [tabFromUrl]);

  useEffect(() => {
    if (activeTab === 'ai-compliance' && user?.id) loadA2pStatus();
  }, [activeTab, user?.id]);

  // Load phone number when phone-number tab is opened
  useEffect(() => {
    if (activeTab !== 'phone-number' || !user?.id) return;
    setPhoneCountry(resolvePhoneCountry(userProfile));
    const load = async () => {
      setPhoneLoading(true);
      try {
        const headers = await getAuthHeaders();
        const res = await fetch('/api/agents/assign-phone-number', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({ agentId: user.id }),
        });
        const data = await res.json();
        if (res.ok && data.phoneNumber) {
          setPhoneNumber(data.phoneNumber);
        } else {
          setPhoneNumber(null);
        }
      } catch {
        setPhoneNumber(null);
      } finally {
        setPhoneLoading(false);
      }
    };
    load();
  }, [activeTab, user?.id, userProfile]);

  const getAreaCodeHeaders = useCallback(async () => ({
    'Content-Type': 'application/json',
    ...(await getAuthHeaders()),
  }), []);

  const handleChangePhoneNumber = async () => {
    const cleaned = newAreaCode.replace(/\D/g, '').slice(0, 3);
    if (cleaned.length !== 3) {
      setPhoneMessage({ type: 'error', text: 'Please enter a valid 3-digit area code.' });
      return;
    }
    setPhoneChanging(true);
    setPhoneMessage(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/agents/change-phone-number', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ areaCode: cleaned, country: phoneCountry }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setPhoneNumber(data.phoneNumber);
        setPhoneMessage({ type: 'success', text: `Number updated to ${data.phoneNumber}` });
        setPhoneChangeMode(false);
        setNewAreaCode('');
        reloadProfile?.();
      } else {
        setPhoneMessage({ type: 'error', text: data.error || 'Could not change number. Try again.' });
      }
    } catch (err) {
      setPhoneMessage({ type: 'error', text: err.message || 'Could not change number.' });
    } finally {
      setPhoneChanging(false);
    }
  };

  const handleAssignPhoneNumber = async () => {
    const cleaned = newAreaCode.replace(/\D/g, '').slice(0, 3);
    if (cleaned.length !== 3) {
      setPhoneMessage({ type: 'error', text: 'Please enter a valid 3-digit area code.' });
      return;
    }
    if (!user?.id) return;
    setPhoneChanging(true);
    setPhoneMessage(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/agents/assign-phone-number', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ agentId: user.id, areaCode: cleaned, country: phoneCountry }),
      });
      const data = await res.json();
      if (res.ok && data.phoneNumber) {
        setPhoneNumber(data.phoneNumber);
        setPhoneMessage({ type: 'success', text: `Number assigned: ${data.phoneNumber}` });
        setNewAreaCode('');
        reloadProfile?.();
      } else {
        const alt = data.alternatives?.length
          ? ` Try: ${data.alternatives.slice(0, 5).join(', ')}`
          : '';
        setPhoneMessage({
          type: 'error',
          text: (data.error || data.message || 'Could not assign number.') + alt,
        });
      }
    } catch (err) {
      setPhoneMessage({ type: 'error', text: err.message || 'Could not assign number.' });
    } finally {
      setPhoneChanging(false);
    }
  };

  // Populate profile form whenever userProfile loads
  useEffect(() => {
    if (userProfile) {
      setProfileForm({
        fullName: userProfile.full_name || '',
        phone: userProfile.phone || '',
      });
    }
  }, [userProfile]);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMessage(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/user/basic-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          fullName: profileForm.fullName.trim(),
          phone: profileForm.phone.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setProfileMessage({ type: 'error', text: data.error || 'Failed to save.' });
      } else {
        setProfileMessage({ type: 'success', text: 'Profile saved.' });
        reloadProfile();
        setTimeout(() => setProfileMessage(null), 3000);
      }
    } catch (err) {
      setProfileMessage({ type: 'error', text: 'Something went wrong. Please try again.' });
    } finally {
      setProfileSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Settings - Nurture Buddy.ai</title>
        <meta name="description" content="Configure your Nurture Buddy.ai settings" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16 pb-20 md:pb-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="font-heading text-3xl font-bold text-foreground tracking-tight">Settings</h1>
              <p className="text-muted-foreground mt-2">
                Manage your account settings and preferences
              </p>
            </div>

            {/* Tabs */}
            <div className="border-b border-border mb-8">
              <nav className="flex overflow-x-auto scrollbar-none -mb-px">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center gap-1.5 py-4 px-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap shrink-0 touch-target
                      ${activeTab === tab.id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                      }
                    `}
                  >
                    <Icon name={tab.icon} size={16} />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden text-xs">{tab.label.split(' ')[0]}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="mt-8">
              {activeTab === 'location-schedule' && (
                <LocationTimezoneSettings />
              )}
              
              {activeTab === 'message-personalization' && (
                <PersonalProfileSettings />
              )}

              {activeTab === 'phone-number' && (
                <div className="bg-card border border-border rounded-lg p-6 max-w-2xl">
                  <h3 className="text-lg font-semibold text-foreground mb-1">Phone Number</h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    Your dedicated SMS number. You can change area codes at any time ($1.15/month billed when the new number is purchased).
                  </p>

                  {phoneLoading ? (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="Loader2" size={18} className="animate-spin" />
                      <span className="text-sm">Loading…</span>
                    </div>
                  ) : phoneNumber ? (
                    <div className="space-y-4">
                      <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 flex items-center justify-between">
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Current number</p>
                          <p className="text-xl font-bold text-primary tracking-wide">{phoneNumber}</p>
                        </div>
                        <Icon name="Phone" size={20} className="text-muted-foreground" />
                      </div>

                      {!phoneChangeMode ? (
                        <Button variant="outline" size="sm" onClick={() => {
                          setPhoneChangeMode(true);
                          setPhoneMessage(null);
                          setNewAreaCode('');
                          setPhoneCountry(resolvePhoneCountry(userProfile));
                          setAreaCodePickerKey((k) => k + 1);
                        }}>
                          Change area code
                        </Button>
                      ) : (
                        <div className="space-y-4">
                          <p className="text-sm text-foreground font-medium">Choose a new area code</p>
                          <AreaCodePicker
                            key={areaCodePickerKey}
                            value={newAreaCode}
                            onChange={setNewAreaCode}
                            country={phoneCountry}
                            onCountryChange={setPhoneCountry}
                            getHeaders={getAreaCodeHeaders}
                            onStatusChange={({ checkingAvailability }) => setCheckingAreaCode(checkingAvailability)}
                            unavailableHint="Pick an alternative above, or try a nearby area code. Your current number will be released when you confirm."
                          />
                          <div className="flex flex-wrap gap-2 pt-2">
                            <Button
                              onClick={handleChangePhoneNumber}
                              loading={phoneChanging}
                              disabled={newAreaCode.length !== 3 || phoneChanging || checkingAreaCode}
                              size="sm"
                            >
                              Confirm change
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setPhoneChangeMode(false);
                                setNewAreaCode('');
                                setPhoneMessage(null);
                                setCheckingAreaCode(false);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Your current number will be released and a new one purchased in the selected area code ($1.15/month).
                            Heads up: all past clients will start receiving your texts from the new number. Each contact will
                            automatically get a quick re-introduction text from the new number so they know it's you.
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (userProfile?.a2p_status === 'approved' || userProfile?.campaign_status === 'VERIFIED') ? (
                    <div className="space-y-4">
                      <div className="rounded-lg border border-border bg-muted/30 px-4 py-3">
                        <p className="text-sm text-foreground font-medium mb-1">No number assigned yet</p>
                        <p className="text-sm text-muted-foreground">
                          You’re A2P approved. Pick an area code below to get your SMS number.
                        </p>
                      </div>
                      <AreaCodePicker
                        key={areaCodePickerKey}
                        value={newAreaCode}
                        onChange={setNewAreaCode}
                        country={phoneCountry}
                        onCountryChange={setPhoneCountry}
                        getHeaders={getAreaCodeHeaders}
                        onStatusChange={({ checkingAvailability }) => setCheckingAreaCode(checkingAvailability)}
                        unavailableHint="Pick an alternative above, or try a nearby area code."
                      />
                      <Button
                        onClick={handleAssignPhoneNumber}
                        loading={phoneChanging}
                        disabled={newAreaCode.length !== 3 || phoneChanging || checkingAreaCode}
                        size="sm"
                      >
                        Assign number
                      </Button>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-border bg-muted/30 px-4 py-3">
                      <p className="text-sm text-muted-foreground">No number assigned yet. Complete AI Compliance setup and your number will be assigned automatically upon approval.</p>
                    </div>
                  )}

                  {phoneMessage && (
                    <p className={`text-sm mt-3 ${phoneMessage.type === 'success' ? 'text-green-600' : 'text-destructive'}`}>
                      {phoneMessage.text}
                    </p>
                  )}
                </div>
              )}

              {activeTab === 'ai-compliance' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">AI Compliance</h3>
                  <p className="text-muted-foreground text-sm mb-6">
                    One form. We handle carrier registration. Status below.
                  </p>
                  {a2pLoading ? (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="Loader2" size={20} className="animate-spin" />
                      <span>Loading status...</span>
                    </div>
                  ) : a2pStatus && (a2pStatus.a2pStatus === 'approved' || a2pStatus.campaignStatus === 'VERIFIED') ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-green-600">
                        <Icon name="CheckCircle" size={24} />
                        <span className="font-medium">Approved</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Brand:</span>{' '}
                          {a2pStatus.brandStatus || '—'}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Campaign:</span>{' '}
                          {a2pStatus.campaignStatus || '—'}
                        </div>
                        {(a2pStatus.approvedAt || a2pStatus.campaignStatusUpdatedAt) && (
                          <div className="sm:col-span-2 text-muted-foreground">
                            Last updated: {a2pStatus.campaignStatusUpdatedAt || a2pStatus.approvedAt || '—'}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : a2pStatus && (a2pStatus.a2pStatus === 'pending' || a2pStatus.brandStatus || a2pStatus.campaignStatus) ? (
                    (() => {
                      const brandFailed = a2pStatus.brandStatus === 'FAILED';
                      return (
                        <div className="space-y-4">
                          <div className={`flex items-center gap-2 ${brandFailed ? 'text-destructive' : 'text-amber-600'}`}>
                            <Icon name={brandFailed ? 'AlertCircle' : 'Clock'} size={24} />
                            <span className="font-medium">{brandFailed ? 'Brand registration failed' : 'Under review'}</span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Brand:</span>{' '}
                              <span className={a2pStatus.brandStatus === 'FAILED' ? 'text-destructive font-medium' : ''}>{a2pStatus.brandStatus || '—'}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Campaign:</span>{' '}
                              {a2pStatus.campaignStatus || '—'}
                            </div>
                            {brandFailed ? (
                              <div className="sm:col-span-2 space-y-3">
                                {a2pStatus.failureReason && (
                                  <p className="text-sm text-muted-foreground">
                                    <span className="font-medium">Reason:</span> {a2pStatus.failureReason}
                                  </p>
                                )}
                                <p className="text-sm text-muted-foreground">
                                  The brand was rejected. This usually happens when the Customer Profile bundle wasn't fully approved before registration. Click below to start over.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  <Button
                                    onClick={loadA2pStatus}
                                    disabled={a2pLoading}
                                    variant="outline"
                                    size="sm"
                                  >
                                    {a2pLoading ? 'Refreshing...' : 'Refresh'}
                                  </Button>
                                  <Button
                                    onClick={handleA2pReset}
                                    disabled={resetting}
                                    variant="destructive"
                                    size="sm"
                                  >
                                    {resetting ? 'Resetting...' : 'Start Over'}
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="sm:col-span-2 space-y-3">
                                <p className="text-muted-foreground">
                                  Your registration has been submitted successfully. Brand approval typically takes 1–2 business days; campaign approval can take up to 10–15 days. You'll be notified by email once you're approved to send messages.
                                </p>
                                <Button
                                  onClick={loadA2pStatus}
                                  disabled={a2pLoading}
                                  variant="outline"
                                  size="sm"
                                >
                                  {a2pLoading ? 'Refreshing...' : 'Refresh status'}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()
                  ) : a2pStatus && a2pStatus.a2pStatus === 'failed' ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-destructive">
                        <Icon name="AlertCircle" size={24} />
                        <span className="font-medium">Registration failed</span>
                      </div>
                      {a2pStatus.failureReason && (
                        <p className="text-sm text-muted-foreground mb-3">{a2pStatus.failureReason}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <Button onClick={handleA2pReset} disabled={resetting} variant="destructive" size="sm">
                          {resetting ? 'Resetting...' : 'Start Over'}
                        </Button>
                      </div>
                      <AIComplianceWidget onComplete={handleA2pComplete} compact />
                    </div>
                  ) : (
                    <AIComplianceWidget onComplete={handleA2pComplete} compact />
                  )}
                </div>
              )}
              
              {activeTab === 'profile' && (
                <div className="bg-card border border-border rounded-lg p-6 max-w-lg">
                  <h3 className="text-lg font-semibold text-foreground mb-1">Profile</h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    Update your name and the mobile number where we send reply alerts.
                  </p>

                  <form onSubmit={handleProfileSave} className="space-y-5">
                    <Input
                      label="Full Name"
                      type="text"
                      value={profileForm.fullName}
                      onChange={(e) => setProfileForm(p => ({ ...p, fullName: e.target.value }))}
                      placeholder="Your full name"
                      required
                      disabled={profileSaving}
                    />

                    <div>
                      <Input
                        label="Your Mobile Number"
                        type="tel"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                        placeholder="+1 (555) 000-0000"
                        disabled={profileSaving}
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        We'll text you here when a client replies to a touchpoint.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Email</label>
                      <p className="text-sm text-muted-foreground bg-muted/50 px-3 py-2 rounded-md border border-border">
                        {user?.email}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">Email cannot be changed here.</p>
                    </div>

                    {profileMessage && (
                      <div className={`flex items-center gap-2 text-sm ${profileMessage.type === 'success' ? 'text-green-600' : 'text-destructive'}`}>
                        <Icon name={profileMessage.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
                        {profileMessage.text}
                      </div>
                    )}

                    <Button type="submit" disabled={profileSaving} className="w-full sm:w-auto">
                      {profileSaving ? 'Saving…' : 'Save Profile'}
                    </Button>
                  </form>
                </div>
              )}
              
              {activeTab === 'billing' && (
                <BillingTab
                  userProfile={userProfile}
                  billingLoading={billingLoading}
                  setBillingLoading={setBillingLoading}
                  billingMessage={billingMessage}
                  setBillingMessage={setBillingMessage}
                />
              )}

              {activeTab === 'notifications' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Notification Preferences</h3>
                  <p className="text-muted-foreground">Notification settings coming soon...</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default Settings;
